import { ChevronRight } from 'lucide-react';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Dialog, DialogContent, DialogTitle } from '@ui/components/base/dialog';
import { Div } from '@ui/components/base/div';
import { ScrollArea } from '@ui/components/base/scroll-area';
import { LoaderButton } from '@ui/components/buttons/loaderButton';
import { ArrowLeftIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

interface DialogSection {
  id: string;
  label: string;
  hasError?: boolean;
  content: ReactNode;
}

interface DialogWithSectionsProps {
  open: boolean;
  title: string;
  sections: DialogSection[];
  activeSectionId: string;
  onSectionChange: (sectionId: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  showErrors?: boolean;
}

const CONTENT_CLASSNAME = cn(
  'inset-0 top-0 left-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0',
  'rounded-none bg-muted p-0 ring-0 sm:max-w-none'
);
const HEADER_CLASSNAME = 'h-20 shrink-0 items-center justify-between border-b border-border bg-background px-6';
const NAV_ITEM_CLASSNAME = cn(
  'flex w-full items-center justify-between rounded-xl border border-transparent px-4 py-2.5',
  'text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-background'
);
const NAV_ITEM_ACTIVE_CLASSNAME = 'border-border bg-background text-foreground';
const NAV_ITEM_ERROR_CLASSNAME = 'border-destructive bg-destructive/10 text-destructive';

const DialogWithSections = ({
  open,
  title,
  sections,
  activeSectionId,
  onSectionChange,
  onBack,
  onSubmit,
  isSubmitting = false,
  showErrors = false,
}: DialogWithSectionsProps) => {
  const { formatMessage } = useIntl();
  const activeSection = sections.find((section) => section.id === activeSectionId);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen: boolean) => {
        if (!nextOpen) {
          onBack();
        }
      }}
    >
      <DialogContent showCloseButton={false} className={CONTENT_CLASSNAME} style={{ borderRadius: 0 }}>
        <Div row className={HEADER_CLASSNAME}>
          <Div row className="items-center gap-3">
            <Button
              type="button"
              variant={ButtonVariant.GHOST}
              size="icon"
              onClick={onBack}
              aria-label={formatMessage({ id: 'back' })}
            >
              <ArrowLeftIcon className="size-4 fill-current" />
            </Button>
            <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          </Div>
          <LoaderButton type="button" loading={isSubmitting} onClick={onSubmit}>
            {formatMessage({ id: 'save' })}
          </LoaderButton>
        </Div>
        <ScrollArea className="min-h-0 flex-1">
          <Div row className="mx-auto w-full max-w-275 items-start gap-8 px-6 py-6">
            <nav className="sticky top-0 flex w-60 shrink-0 flex-col gap-1">
              {sections.map((section) => {
                const isActive = section.id === activeSectionId;
                const isInError = showErrors && section.hasError === true;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => onSectionChange(section.id)}
                    className={cn(
                      NAV_ITEM_CLASSNAME,
                      isActive && NAV_ITEM_ACTIVE_CLASSNAME,
                      isInError && NAV_ITEM_ERROR_CLASSNAME
                    )}
                  >
                    <span className="truncate">{section.label}</span>
                    {isActive && <ChevronRight className="size-4 shrink-0" />}
                  </button>
                );
              })}
            </nav>
            <Div column className="min-w-0 flex-1 rounded-xl bg-background p-6 ring-1 ring-foreground/10">
              {activeSection?.content}
            </Div>
          </Div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export { DialogWithSections };
export type { DialogSection, DialogWithSectionsProps };
